import React, { useEffect, useId, useRef, useState } from 'react';
import { TrendingUp, Activity, Database, Zap } from 'lucide-react';

interface MarketLoadingScreenProps {
  onComplete?: () => void;
}

const LOADING_MESSAGES = [
  { text: 'Connecting to market feeds...', icon: Activity },
  { text: 'Loading real-time prices...', icon: TrendingUp },
  { text: 'Preparing trading pairs...', icon: Database },
  { text: 'Synchronizing wallet data...', icon: Zap },
  { text: 'Finalizing setup...', icon: Activity }
];

const MARKET_ACTIVITY_TONES = [
  'cool',
  'violet',
  'cool',
  'violet',
  'cool',
  'violet',
  'cool',
  'violet',
  'cool'
] as const;

const CHART_LEFT = 10;
const CHART_RIGHT = 90;
const CHART_TOP = 20;
const CHART_BOTTOM = 82;
const CHART_BASELINE = 92;
const GRAPH_CYCLES = [0, 1] as const;

const createChartPoints = (bars: readonly number[]) =>
  bars.map((value, index) => {
    const x = CHART_LEFT + (index / (bars.length - 1)) * (CHART_RIGHT - CHART_LEFT);
    const y = CHART_BOTTOM - (value / 100) * (CHART_BOTTOM - CHART_TOP);

    return {
      value,
      tone: MARKET_ACTIVITY_TONES[index],
      x: Number(x.toFixed(2)),
      y: Number(y.toFixed(2)),
    };
  });

const createChartLinePath = (bars: readonly number[]) =>
  createChartPoints(bars)
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

const createChartAreaPath = (bars: readonly number[]) => {
  const linePath = createChartLinePath(bars);
  return `${linePath} L ${CHART_RIGHT} ${CHART_BASELINE} L ${CHART_LEFT} ${CHART_BASELINE} Z`;
};

const MARKET_GRAPH_VALUES = [24, 52, 44, 68, 56, 78, 64, 86, 72] as const;
const MARKET_GRAPH_POINTS = createChartPoints(MARKET_GRAPH_VALUES);
const MARKET_GRAPH_LINE_PATH = createChartLinePath(MARKET_GRAPH_VALUES);
const MARKET_GRAPH_AREA_PATH = createChartAreaPath(MARKET_GRAPH_VALUES);

const MarketLoadingScreen: React.FC<MarketLoadingScreenProps> = ({ onComplete }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const hasCompletedRef = useRef(false);
  const chartId = useId().replace(/:/g, '');
  const signalGradientId = `${chartId}-signal-gradient`;
  const areaGradientId = `${chartId}-area-gradient`;

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => {
        if (prev >= LOADING_MESSAGES.length - 1) {
          clearInterval(messageInterval);
          return prev;
        }
        return prev + 1;
      });
    }, 400);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 40);

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
    };
  }, []);

  useEffect(() => {
    if (progress < 100 || hasCompletedRef.current) {
      return;
    }

    const delayTimer = setTimeout(() => {
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true;
        onComplete?.();
      }
    }, 220);

    return () => clearTimeout(delayTimer);
  }, [progress, onComplete]);

  const CurrentIcon = LOADING_MESSAGES[messageIndex].icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-slate-950">
      <div className="market-loading-surface absolute inset-0"></div>
      <div className="market-loading-backdrop-grid absolute inset-0"></div>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="market-loading-glow market-loading-glow-a"></div>
        <div className="market-loading-glow market-loading-glow-b"></div>
        <div className="market-loading-glow market-loading-glow-c"></div>
      </div>

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="rounded-[2rem] border border-violet-300/10 bg-slate-950/48 px-6 py-7 text-center shadow-[0_28px_100px_rgba(2,6,23,0.62)] backdrop-blur-xl">
          <div className="mb-4 inline-flex items-center rounded-full border border-violet-300/12 bg-violet-500/8 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-violet-100/80">
            Market Graph Sync
          </div>

          <div className="mx-auto mb-6 w-full max-w-sm rounded-[1.5rem] border border-violet-300/10 bg-gradient-to-br from-slate-950/78 via-violet-950/10 to-slate-950/72 p-4">
            <div className="mb-3 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
              <span>Live Market</span>
              <div className="flex items-center gap-2">
                <CurrentIcon size={14} className="text-violet-200" />
                <span>{Math.round(progress)}%</span>
              </div>
            </div>

            <div className="market-loading-chart-frame relative h-36 overflow-hidden rounded-[1.15rem] border border-violet-300/10 bg-slate-950/70">
              <div className="market-loading-chart-grid absolute inset-0"></div>
              <div className="market-loading-chart-scan absolute inset-y-0 w-16"></div>
              <div className="market-loading-chart-viewport absolute inset-0 overflow-hidden">
                <div className="market-loading-graph-track absolute inset-y-0 left-0">
                  {GRAPH_CYCLES.map((cycle) => {
                    const cycleSignalGradientId = `${signalGradientId}-${cycle}`;
                    const cycleAreaGradientId = `${areaGradientId}-${cycle}`;

                    return (
                      <div key={cycle} className="market-loading-graph-cycle relative">
                        <svg
                          className="market-loading-signal-layer absolute inset-0 z-20 h-full w-full"
                          viewBox="0 0 100 100"
                          preserveAspectRatio="none"
                          aria-hidden="true"
                        >
                          <defs>
                            <linearGradient id={cycleSignalGradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.35" />
                              <stop offset="35%" stopColor="#7dd3fc" stopOpacity="0.9" />
                              <stop offset="72%" stopColor="#c4b5fd" stopOpacity="0.96" />
                              <stop offset="100%" stopColor="#f472b6" stopOpacity="0.7" />
                            </linearGradient>
                            <linearGradient id={cycleAreaGradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.24" />
                              <stop offset="58%" stopColor="#6366f1" stopOpacity="0.12" />
                              <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
                            </linearGradient>
                          </defs>

                          <path
                            className="market-loading-signal-area"
                            d={MARKET_GRAPH_AREA_PATH}
                            fill={`url(#${cycleAreaGradientId})`}
                          ></path>
                          <path className="market-loading-signal-glow" d={MARKET_GRAPH_LINE_PATH}></path>
                          <path
                            className="market-loading-signal-line"
                            d={MARKET_GRAPH_LINE_PATH}
                            stroke={`url(#${cycleSignalGradientId})`}
                          ></path>
                          <path className="market-loading-signal-trace" d={MARKET_GRAPH_LINE_PATH}></path>

                          {MARKET_GRAPH_POINTS.map((point, index) => (
                            <circle
                              key={`signal-point-${cycle}-${index}`}
                              cx={point.x}
                              cy={point.y}
                              r={index === 0 ? 2.3 : 1.7}
                              className={`market-loading-signal-point ${index === 0 ? 'is-active' : ''}`}
                              style={{ animationDelay: `-${index * 0.16}s` }}
                            ></circle>
                          ))}
                        </svg>

                        <div className="market-loading-bars relative z-10 mx-auto flex h-full max-w-[16rem] items-end gap-2 px-3 pb-4 pt-10">
                          {MARKET_GRAPH_POINTS.map((bar, index) => (
                            <div key={`market-bar-${cycle}-${index}`} className="market-loading-bar-slot">
                              <div
                                className={`market-loading-bar ${bar.tone === 'violet' ? 'is-violet' : 'is-cool'}`}
                                style={{
                                  height: `${bar.value}%`,
                                  animationDelay: `-${index * 0.18}s`,
                                }}
                              ></div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="market-loading-live-cursor absolute left-8 top-[58%] z-30">
                <div className="market-loading-live-cursor-ping"></div>
                <div className="market-loading-live-cursor-core"></div>
              </div>

              <div className="market-loading-activity absolute inset-x-3 top-4 z-30 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                <span>Feed Sync</span>
                <span>Realtime</span>
              </div>

              <div className="market-loading-pulse-line absolute inset-x-4 bottom-4 h-px"></div>
            </div>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-white">Getting Markets Ready</h2>

          <div className="mt-3 h-7">
            <p
              key={LOADING_MESSAGES[messageIndex].text}
              className="text-base text-slate-300 animate-fade-in"
            >
              {LOADING_MESSAGES[messageIndex].text}
            </p>
          </div>

          <div className="mt-6">
            <div className="overflow-hidden rounded-full border border-violet-300/10 bg-slate-900/80">
              <div
                className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 via-violet-500 to-fuchsia-400 transition-all duration-300 ease-out shadow-[0_0_22px_rgba(139,92,246,0.35)]"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="mt-2 text-sm text-violet-100/80">{Math.round(progress)}% Complete</p>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-400">
            <div className="flex gap-1.5">
              <div className="h-2.5 w-2.5 rounded-full bg-blue-400/90 animate-pulse"></div>
              <div className="h-2.5 w-2.5 rounded-full bg-violet-400/90 animate-pulse delay-75"></div>
              <div className="h-2.5 w-2.5 rounded-full bg-fuchsia-400/85 animate-pulse delay-150"></div>
            </div>
            <span>Secure market channels established</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketLoadingScreen;
